// MaturaRootBinomialClosedTask.js
// Zadanie maturalne – zamknięte.
// Typ: wyrażenie z pierwiastkiem i wzorem skróconego mnożenia
// np. (√3 + 1)^2 − √12, (√5 − 2)^2 + √20, itp.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import { AlternativeExplanation } from "../../../../matura/MaturaMay2026ClosedTaskSupport";

const LETTERS = ["A", "B", "C", "D"];

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function pairKey(c, a) {
  return `${c}|${a}`;
}

// zapis wyniku w postaci A ± B√p
function formatRootExpr(constTerm, alpha, p) {
  const A = constTerm;
  const B = alpha;
  const sqrtP = `\\sqrt{${p}}`;

  if (B === 0) {
    return String(A);
  }

  const absB = Math.abs(B);
  const rootPart = absB === 1 ? sqrtP : `${absB}${sqrtP}`;

  if (A === 0) {
    return B > 0 ? rootPart : `-${rootPart}`;
  }

  const signSym = B > 0 ? " + " : " - ";
  return `${A}${signSym}${rootPart}`;
}

// ============================================================================
// GENERATOR
// ============================================================================

function createJune2025OriginalTask() {
  const options = [
    "4 - 4\\sqrt{3}",
    "4 - 2\\sqrt{3}",
    "2",
    "4",
  ].map((latex, index) => ({
    label: LETTERS[index],
    latex,
    isCorrect: index === 3,
  }));

  return {
    type: "MaturaRootBinomialClosedTask",
    data: {
      p: 3,
      b: 1,
      c: 2,
      binSign: 1,
      squareSign: 1,
      rootSign: -1,
      squareFirst: true,
      rootRad: 12,
      constTerm: 4,
      alpha: 0,
      exprLatex: "(\\sqrt{3} + 1)^{2} - \\sqrt{12}",
      options,
      correctIndex: 3,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: 4,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_CZE"
  ) {
    return createJune2025OriginalTask();
  }

  // p – liczba pod pierwiastkiem (żeby były sensowne pierwiastki)
  const p = pick([2, 3, 5]);
  // b – liczba w nawiasie (√p ± b)
  const b = pick([1, 2, 3]);

  // c – czynnik przy pierwiastku po uproszczeniu: √(c^2·p) = c√p
  // WAŻNE: żeby pierwiastek zawsze był upraszczalny (nie było √2, √3 "na pusto"),
  // wymuszamy c > 1.
  const c = pick([2, 3]);

  // znak w nawiasie: (√p ± b)
  const binSign = Math.random() < 0.5 ? 1 : -1;

  // losowe znaki i kolejność
  let squareSign = Math.random() < 0.5 ? 1 : -1; // przed kwadratem
  let rootSign = Math.random() < 0.5 ? 1 : -1; // przed pierwiastkiem
  let squareFirst = Math.random() < 0.5 ? true : false; // kto stoi pierwszy

  // ─────────────────────────────────────────────
  // BEZPIECZNIK „bez minusa na początku”:
  // 1) jeśli pierwszy byłby kwadrat z minusem: -(...)^2 + ...
  // 2) albo pierwszy byłby pierwiastek z minusem: -√(...) ± (...)
  // to narzucamy prostszy, maturalny układ:
  // – pierwszy składnik dodatni (nawias do kwadratu),
  // – pierwiastek drugi (może być + lub -).
  // ─────────────────────────────────────────────
  if (
    (squareFirst && squareSign === -1) ||
    (!squareFirst && rootSign === -1)
  ) {
    squareFirst = true; // na początku (√p ± b)^2
    squareSign = 1; // bez minusa przed kwadratem
    // rootSign zostawiamy dowolny – plus/minus przy pierwiastku jest OK
  }

  const rootRad = p * c * c; // np. p=5, c=3 → √45

  // (√p ± b)^2 = p + b^2 ± 2b√p
  const baseConst = p + b * b;
  const crossCoeff = 2 * b * binSign; // współczynnik przy √p

  const squareConst = squareSign * baseConst;
  const squareAlpha = squareSign * crossCoeff;
  const rootAlpha = rootSign * c;

  const constTerm = squareConst;
  const alpha = squareAlpha + rootAlpha;

  const correctLatex = formatRootExpr(constTerm, alpha, p);

  const sqrtP = `\\sqrt{${p}}`;
  const binOp = binSign === 1 ? "+" : "-";
  const binInside = `${sqrtP} ${binOp} ${b}`;
  const binSquared = `(${binInside})^{2}`;

  // budujemy latex całego wyrażenia
  let exprLatex;
  if (squareFirst) {
    // zgodnie z bezpiecznikiem: squareSign = +1
    const between = rootSign === 1 ? "+" : "-";
    exprLatex = `${binSquared} ${between} \\sqrt{${rootRad}}`;
  } else {
    // tu pierwszy jest pierwiastek, ale z gwarancją rootSign = +1
    const first = `\\sqrt{${rootRad}}`;
    const between = squareSign === 1 ? "+" : "-";
    exprLatex = `${first} ${between} ${binSquared}`;
  }

  // ───────── kandydaci na błędne odpowiedzi ─────────

  const pairs = [];
  const used = new Set([pairKey(constTerm, alpha)]);

  const addPair = (c0, a0) => {
    const key = pairKey(c0, a0);
    if (!used.has(key)) {
      used.add(key);
      pairs.push({ c: c0, a: a0 });
    }
  };

  // 1) zapomniany wyraz 2b√p (tylko kwadrat + pierwiastek)
  const missCross_const = squareConst;
  const missCross_alpha = rootAlpha;
  addPair(missCross_const, missCross_alpha);

  // 2) zły znak przed kwadratem (odwracamy squareSign)
  const wrongSquareConst = -squareConst;
  const wrongSquareAlpha = -squareAlpha + rootAlpha;
  addPair(wrongSquareConst, wrongSquareAlpha);

  // 3) błędne uproszczenie √(c^2·p) → c^2√p
  const wrongRootAlpha = squareAlpha + rootSign * c * c;
  addPair(squareConst, wrongRootAlpha);

  // jeśli nadal mamy mniej niż 3 różne błędne – dosypujemy w pobliżu
  while (pairs.length < 3) {
    const deltaConst = pick([-2, -1, 1, 2]);
    const deltaAlpha = pick([-2, -1, 1, 2]);
    addPair(constTerm + deltaConst, alpha + deltaAlpha);
  }

  const optionsRaw = [
    { latex: correctLatex, isCorrect: true },
    ...pairs.slice(0, 3).map((p0) => ({
      latex: formatRootExpr(p0.c, p0.a, p),
      isCorrect: false,
    })),
  ];

  const shuffled = shuffle(optionsRaw).map((o, idx) => ({
    label: LETTERS[idx],
    latex: o.latex,
    isCorrect: o.isCorrect,
  }));

  const correctIndex = shuffled.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaRootBinomialClosedTask",
    data: {
      p,
      b,
      c,
      binSign,
      squareSign,
      rootSign,
      squareFirst,
      rootRad,
      constTerm,
      alpha,
      exprLatex,
      options: shuffled,
      correctIndex,
    },
  };
}

// ============================================================================
// TREŚĆ
// ============================================================================

export function renderTask({ data }) {
  const { exprLatex, options } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Liczba <InlineMath math={exprLatex} /> jest równa:
      </p>
      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// INPUT (ABCD)
// ============================================================================

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
    });
  };

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// ============================================================================
// CHECK / ODPOWIEDŹ
// ============================================================================

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options[data.correctIndex];
  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    !userAnswer.latex
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;
  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}

// ============================================================================
// WYJAŚNIENIE
// ============================================================================

export function renderExplanation({ data }) {
  const {
    p,
    b,
    c,
    binSign,
    squareSign,
    rootSign,
    rootRad,
    constTerm,
    alpha,
    exprLatex,
    squareFirst,
  } = data;

  const sqrtP = `\\sqrt{${p}}`;
  const binOp = binSign === 1 ? "+" : "-";
  const binInside = `${sqrtP} ${binOp} ${b}`;
  const binSquared = `(${binInside})^{2}`;

  // Wzór zależny od tego, czy mamy sumę czy różnicę
  const formulaLatex =
    binSign === 1
      ? "(a + b)^2 = a^2 + 2ab + b^2"
      : "(a - b)^2 = a^2 - 2ab + b^2";

  const crossSignSym = binSign === 1 ? "+" : "-";
  const crossCoeffAbs = 2 * b;
  const b2 = b * b;

  // (√p ± b)^2 -> p + b^2 ± 2b√p
  const baseConst = p + b2;
  const crossCoeff = 2 * b * binSign;

  // 1) pokazujemy (sqrt(p))^2 w nawiasie
  const expanded1 = `(${sqrtP})^{2} ${crossSignSym} 2\\cdot ${sqrtP}\\cdot ${b} + ${b}^{2}`;
  // 2) tu od razu liczymy b^2 (np. 3^2 -> 9)
  const expanded2 = `${p} ${crossSignSym} ${crossCoeffAbs}${sqrtP} + ${b2}`;
  // 3) łączymy wyrazy stałe
  const expanded3 =
    crossSignSym === "+"
      ? `${baseConst} + ${crossCoeffAbs}${sqrtP}`
      : `${baseConst} - ${crossCoeffAbs}${sqrtP}`;

  const squareSimplLatex = formatRootExpr(baseConst, crossCoeff, p);

  const rootSimplified = `${c}${sqrtP}`;

  const resultLatex = formatRootExpr(constTerm, alpha, p);

  // Linijka łączenia w kroku 3 (z pokazaniem redukcji części pierwiastkowej)
  // Uwaga: konstruujemy "faktyczne" dwa składniki w kolejności jak w zadaniu.
  let combineLine1 = "";
  let combineLine2 = "";

  if (squareFirst) {
    // zgodnie z bezpiecznikiem: squareSign = +1
    const between = rootSign === 1 ? "+" : "-";

    // Tu pokazujemy najpierw: (po kroku1) ± (po kroku2)
    combineLine1 = `${squareSimplLatex} ${between} ${rootSimplified}`;

    // A potem: stałe +/− pierwiastki wprost (żeby było np. 6 - 2√5 - 3√5)
    const sqA = baseConst;
    const sqB = crossCoeff; // może być ujemne
    const rootB = rootSign * c;

    const sqPart2 = formatRootExpr(sqA, sqB, p); // np. 6 - 2√5

    // Składamy: (6 - 2√5) + (-3√5) -> 6 - 2√5 - 3√5 (czytelnie)
    // Żeby nie było "+ -3√5", robimy to jako dwa składniki obok.
    combineLine2 = `${sqPart2} ${rootSign === 1 ? "+" : "-"} ${formatRootExpr(0, Math.abs(rootB), p)}`;
    // jeśli rootSign = -1, to powyższe daje "6 - 2√5 - 3√5" (bo between = "-")
    // jeśli rootSign = +1, to "6 - 2√5 + 3√5"
  } else {
    // rootFirst: rootSign = +1 (z bezpiecznika)
    const between = squareSign === 1 ? "+" : "-";

    // (po kroku2) ± (po kroku1)
    combineLine1 = `${rootSimplified} ${between} \\left(${squareSimplLatex}\\right)`;

    // Druga linijka: rozwinięcie znaków: np. 2√5 - (6 - 2√5) -> 2√5 - 6 + 2√5
    // Tu jednak trzymamy maturalną czytelność: pokazujemy stałe i pierwiastki osobno.
    // Wystarczy analogicznie jak wyżej:
    const rootB = c; // dodatni
    const sqA = baseConst;

    const rootPart2 = formatRootExpr(0, rootB, p); // np. 3√5
    const signedCross = squareSign * crossCoeff;
    combineLine2 = `${rootPart2} ${between} ${sqA} ${signedCross >= 0 ? "+" : "-"} ${formatRootExpr(0, Math.abs(signedCross), p)}`;
  }

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Rozwijamy nawias <InlineMath math={binSquared} />{" "}
        korzystając ze wzoru <InlineMath math={formulaLatex} />:
      </p>
      <BlockMath math={`${binSquared} = ${expanded1} =`} />
      <BlockMath math={`= ${expanded2} =`} />
      <BlockMath math={`= ${expanded3}`} />

      {squareSign === -1 && (
        <>
          <p>
            Jeśli przy nawiasie do kwadratu występuje znak minus, to zmienia on znaki wszystkich składników.
          </p>
          <BlockMath math={`-(${expanded3})`} />
        </>
      )}
      <p>
        <strong>Krok 2.</strong> Uproszczamy pierwiastek{" "}
        <InlineMath math={`\\sqrt{${rootRad}}`} />:
      </p>
      <BlockMath
        math={`\\sqrt{${rootRad}} = \\sqrt{${c}^{2}\\cdot ${p}} = ${c}\\sqrt{${p}}`}
      />
      <p>
        <strong>Krok 3.</strong> Łączymy otrzymane wyrażenia:
      </p>
      <BlockMath math={`${exprLatex} = ${combineLine1} =`} />
      <BlockMath math={`= ${combineLine2} =`} />
      <BlockMath math={`= ${resultLatex}`} />

      <AlternativeExplanation
        solution={{
          title: "Inny sposób: najpierw upraszczamy pierwiastek",
          intro: "Kolejność działań możemy odwrócić: najpierw upraszczamy pojedynczy pierwiastek, a dopiero później korzystamy ze wzoru skróconego mnożenia.",
          steps: [
            {
              title: "Upraszczamy pojedynczy pierwiastek.",
              latex: `\\sqrt{${rootRad}}=\\sqrt{${c}^{2}\\cdot${p}}=${c}\\sqrt{${p}}`,
            },
            {
              title: "Rozwijamy kwadrat ze wzoru skróconego mnożenia.",
              latex: `${binSquared}=${expanded1}=${expanded2}=${expanded3}`,
            },
            {
              title: "Podstawiamy otrzymane postacie i redukujemy wyrazy podobne.",
              latex: `${exprLatex}=${combineLine1}=${combineLine2}=${resultLatex}`,
            },
          ],
          note: "To ten sam rachunek wykonany w innej, równie poprawnej kolejności.",
        }}
      />
    </div>
  );
}
